import DocumentBody from '@/components/DocumentBody/DocumentBody';
import DocumentHeader from '@/components/DocumentHeader/DocumentHeader';
import LoaderTransparent from '@/components/Loader/LoaderTransparent/LoaderTransparent';
import { getAllOrganizationDocuments } from '@/lib/functions/apiCalls';
import { ErrorHandle, handleResponse } from '@/lib/functions/funcUtils';
import { useAppDispatch } from '@/lib/hooks/hooks';
import { Document as DocumentType, SortingType } from '@/lib/types/apiTypes';
import { useState } from 'react';
import { useInfiniteQuery } from 'react-query';

const pageSize = 12;

const Document = () => {
	const dispatch = useAppDispatch();

	const [sortOrder, setSortOrder] = useState<SortingType>('DESC');
	const [selectedCategory, setSelectedCategory] = useState<string>('none');
	const [searchTerm, setSearchTerm] = useState<string>('');

	const fetchDocs = async (pageParams = 1) => {
		try {
			const response = await getAllOrganizationDocuments({
				pageNumber: pageParams,
				pageSize: pageSize,
				sortOrder: sortOrder,
				...(selectedCategory !== 'none' && { category: selectedCategory }),
				...(searchTerm !== '' && { search: searchTerm }),
			});
			handleResponse(response);
			return {
				documents: (response?.data as { documents: DocumentType[] })?.documents,
				nextCursor: (response?.data as any).currentPage + 1,
				pageParams: {
					pageNumber: (response?.data as any).currentPage,
					totalRecords: (response?.data as any).totalRecords,
				},
			};
		} catch (error: unknown) {
			ErrorHandle(dispatch, error);
		}
	};

	const {
		data,
		fetchNextPage,
		isFetchingNextPage,
		isLoading,
		isFetching,
		isFetchedAfterMount,
	} = useInfiniteQuery({
		queryKey: ['documents', sortOrder, selectedCategory, searchTerm],
		queryFn: ({ pageParam }) => fetchDocs(pageParam),
		getNextPageParam: (lastPage) => lastPage?.nextCursor || 1,
		refetchOnWindowFocus: false,
	});

	// formating data from pages to documents array
	const documents =
		(data?.pages?.map((page) => page?.documents).flat() as DocumentType[]) ||
		[];
	const documentsCount = data?.pages?.[0]?.pageParams?.totalRecords;

	return (
		<div className="bg-[#f5f5f5] rounded-tl-[30px] min-h-screen ">
			{!data?.pages?.[0]?.documents && isLoading && isFetchedAfterMount ? (
				<LoaderTransparent />
			) : (
				<>
					<DocumentHeader
						selectedCategory={selectedCategory}
						setSelectedCategory={setSelectedCategory}
						handleSearchChange={(searchTerm: string) =>
							setSearchTerm(searchTerm)
						}
					/>
					<div className="flex flex-col h-[calc(100vh-198px)]">
						<DocumentBody
							fetchNextPage={fetchNextPage}
							documentsCount={documentsCount}
							documents={documents}
							sortOrder={sortOrder}
							handleSorting={setSortOrder}
							isFetchingNextPage={isFetchingNextPage}
							showLoadMore={
								documentsCount >= pageSize &&
								documents.length >= pageSize &&
								documentsCount > documents.length
							}
						/>
					</div>
					{isFetching && <LoaderTransparent />}
				</>
			)}
		</div>
	);
};

export default Document;
